import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ClipboardCheck, Users } from 'lucide-react';
import type { InspectorToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDate } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { useInspectorsList, useInspectorMutations } from '~/hooks/use-inspectors';
import { useLinkedCustomers, useToggleLinkedCustomer } from '~/hooks/use-sales-person-links';
import { InspectorEditDialog } from './inspector-edit-dialog';
import { LinkToggleDialog } from '../sales-persons/link-toggle-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Inspectors (Επιθεωρητές) — full CRUD grid with a per-row linked-customers sub-view. */
export function InspectorsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useInspectorsList();
	const { remove } = useInspectorMutations();

	const [editRow, setEditRow] = useState<InspectorToken | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteRow, setDeleteRow] = useState<InspectorToken | null>(null);
	const [customersFor, setCustomersFor] = useState<InspectorToken | null>(null);

	const linkedCustomers = useLinkedCustomers(customersFor?.id ?? null);
	const toggleCustomer = useToggleLinkedCustomer(customersFor?.id ?? null);

	const col = (key: string) => t(`customers.inspectors.fields.${key}`);

	const columns = useMemo<SystemGridColumn<InspectorToken>[]>(
		() => [
			{
				key: 'fn',
				header: col('name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.fn ?? '',
				exportValue: o => o.fn ?? '',
				render: o => <span className="truncate text-sm font-medium text-foreground">{o.fn || '—'}</span>
			},
			{
				key: 'ln',
				header: col('surname'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.ln ?? '',
				exportValue: o => o.ln ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.ln || '—'}</span>
			},
			{
				key: 'eml',
				header: col('email'),
				width: 'minmax(0,1.2fr)',
				sortValue: o => o.eml ?? '',
				exportValue: o => o.eml ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.eml || '—'}</span>
			},
			{
				key: 'tel',
				header: col('tel'),
				width: '140px',
				sortValue: o => o.tel ?? '',
				exportValue: o => o.tel ?? '',
				render: o => <span className="truncate text-sm tabular-nums text-foreground-muted">{o.tel || '—'}</span>
			},
			{
				key: 'mob',
				header: col('mobile'),
				width: '140px',
				sortValue: o => o.mob ?? '',
				exportValue: o => o.mob ?? '',
				render: o => <span className="truncate text-sm tabular-nums text-foreground-muted">{o.mob || '—'}</span>
			},
			{
				key: 'cdt',
				header: col('createDate'),
				width: '170px',
				defaultVisible: false,
				sortValue: o => (o.cdt as string | null) ?? '',
				exportValue: o => formatDate(o.cdt as string | null),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.cdt && formatDate(o.cdt as string)) || '—'}</span>
			},
			{
				key: 'iv',
				header: col('visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	const fullName = (i: InspectorToken) => [i.fn, i.ln].filter(Boolean).join(' ').trim();

	return (
		<>
			<SystemGrid<InspectorToken>
				gridId="inspectors"
				title={t('customers.inspectors.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? `${o.fn}-${o.ln}`}
				columns={columns}
				searchPredicate={(o, q) =>
					(o.fn ?? '').toLowerCase().includes(q) || (o.ln ?? '').toLowerCase().includes(q) || (o.eml ?? '').toLowerCase().includes(q)
				}
				defaultSort={{ key: 'ln', dir: 'asc' }}
				emptyIcon={<ClipboardCheck size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('customers.inspectors.add')}
				onAdd={() => {
					setEditRow(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditRow(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={o => (
					<Button
						variant="ghost"
						size="icon"
						aria-label={t('customers.links.customers')}
						title={t('customers.links.customers')}
						onClick={() => setCustomersFor(o)}>
						<Users size={15} />
					</Button>
				)}
				exportFilename="inspectors"
			/>

			<InspectorEditDialog open={editOpen} onOpenChange={setEditOpen} row={editRow} />

			<LinkToggleDialog
				open={Boolean(customersFor)}
				onOpenChange={open => !open && setCustomersFor(null)}
				title={t('customers.links.customersTitle', { name: customersFor ? fullName(customersFor) : '' })}
				rows={linkedCustomers.data ?? []}
				loading={linkedCustomers.isLoading}
				getKey={row => row.id ?? row.cid}
				isLinked={row => Boolean(row.icl)}
				onToggle={(row, next) => toggleCustomer.mutateAsync({ row, linked: next })}
				toggling={toggleCustomer.isPending}
				columns={[
					{ header: t('customers.list.columns.email'), value: row => row.customer?.eml ?? '' },
					{ header: t('customers.list.columns.name'), value: row => row.customer?.fn ?? '' },
					{ header: t('customers.list.columns.surname'), value: row => row.customer?.ln ?? '' }
				]}
				searchText={row => `${row.customer?.eml ?? ''} ${row.customer?.fn ?? ''} ${row.customer?.ln ?? ''}`}
				emptyText={t('common.noResults')}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('customers.inspectors.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('customers.inspectors.deleteBody', { name: deleteRow ? fullName(deleteRow) : '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
